/**
 * PRP-WEB-2 W2.4: the plan editor's keys, pure. Ctrl/Cmd+Z undo, Ctrl+Y or Shift+Ctrl+Z redo,
 * Delete/Backspace delete, Ctrl+C / Ctrl+V a room, Esc cancels a drag (else clears the
 * selection), arrows nudge 1 in (Shift: 1 ft), F2 renames. Nothing while typing in a field.
 */
export type KeyIntent =
  | { kind: 'undo' } | { kind: 'redo' } | { kind: 'delete' } | { kind: 'copy' } | { kind: 'paste' }
  | { kind: 'escape' } | { kind: 'rename' } | { kind: 'nudge'; dx: number; dy: number };

export interface KeyLike { key: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean; target: EventTarget | null }

export const NUDGE_FT = 1 / 12;
export const NUDGE_SHIFT_FT = 1;

export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || !!el.isContentEditable);
}

export function keyIntent(e: KeyLike): KeyIntent | null {
  if (isTypingTarget(e.target)) return null;
  const mod = e.ctrlKey || e.metaKey;
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  if (mod && k === 'z') return e.shiftKey ? { kind: 'redo' } : { kind: 'undo' };
  if (mod && k === 'y') return { kind: 'redo' };
  if (mod && k === 'c') return { kind: 'copy' };
  if (mod && k === 'v') return { kind: 'paste' };
  if (mod) return null;
  if (k === 'Delete' || k === 'Backspace') return { kind: 'delete' };
  if (k === 'Escape') return { kind: 'escape' };
  if (k === 'F2') return { kind: 'rename' };
  const step = e.shiftKey ? NUDGE_SHIFT_FT : NUDGE_FT;
  if (k === 'ArrowLeft') return { kind: 'nudge', dx: -step, dy: 0 };
  if (k === 'ArrowRight') return { kind: 'nudge', dx: step, dy: 0 };
  if (k === 'ArrowUp') return { kind: 'nudge', dx: 0, dy: -step };
  if (k === 'ArrowDown') return { kind: 'nudge', dx: 0, dy: step };
  return null;
}
